import React, { useEffect, useState } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import IntlMessages from '@projectK/helpers/IntlMessages';
import { BiUser } from 'react-icons/bi';
import { AiOutlineLock } from 'react-icons/ai';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import DiscountIcon from '@mui/icons-material/Discount';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import AppAnimate from '@projectK/components/AppAnimate';
import { Fonts, paymentRecovery } from '@projectK/constants/AppEnums';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import {
  BasicSettings,
  VerbiageSettings,
  MissedPaymentSetting
} from '@projectK/modules/General/BasicSettings';
import jwtAxios from '@projectK/services/auth/JWT';
import {
  useRolePermissions,
  hasPermission,
  isStudent,
  isParent,
  isActualSuperAdmin,
  isActualAdmin
} from '@projectK/context/RolesProvider';
import RBACDashboard from '../admin/RolesAndPermissions';
import AuditLog from '../admin/AuditLog';
import { AccountTabsWrapper,Notification,ChangePassword } from '@projectK/modules/MyProfile';
import AccountInfo from '../student/AccountInfo';
import AllWaiverCombine from '../admin/AllWaiverCombine';
import FeeSettings from '../admin/ConvenienceFeeSettings';
import PromoCodes from '../admin/PromoCodes';
import ProfileFiledData from '../admin/ProfileData';
import { RiShieldUserLine } from 'react-icons/ri';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import LinkedAccounts from '../../../../../libs/modules/src/lib/account/MyProfile/LinkedAccounts';
import { useMobileSettings } from '@projectK/context/MobileContextProvider';
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useAddonsSettings } from '@projectK/context/CheckAdddons';
import MyWallet from './MyWallet';
function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const verbiageData = JSON.parse(localStorage.getItem('verbiageSettings')) || {};

// Define tabs with permission key and component
const tabs = (hasPaymentRecoveryAddon) => { 
  return [
  {
    id: 1,
    icon: <BiUser />,
    name: <IntlMessages id='common.organizationInfo' />,
    permissionKey: 'Company Settings - General',
    component: (props) => <BasicSettings {...props} />,
  },
  {
    id: 2,
    icon: <RiShieldUserLine />,
    name: <IntlMessages id='common.accountInfo' />,
    permissionKey: 'My Locker - Account Info',
    component: () => <AccountInfo />,
  },
  {
    id: 21,
    icon: <AccountBalanceWalletIcon />,
    name: 'My Wallet',
    permissionKey: 'My Locker - Account Info',
    component: () => <MyWallet />,
  },
  {
    id: 3,
    icon: <AiOutlineLock />,
    name: <IntlMessages id='common.changePassword' />,
    permissionKey: 'My Locker - Account Info',
    component: () => <ChangePassword />,
  },
  {
    id: 4,
    icon: <AdminPanelSettingsIcon />,
    name: <IntlMessages id='common.rolesAndPermissions' />,
    permissionKey: 'Role And Permissions',
    component: () => <RBACDashboard />,
  },
  {
    id: 5,
    icon: <IoMdInformationCircleOutline />,
    name: <IntlMessages id='common.auditLogs' />,
    permissionKey: 'Audit Logs',
    component: () => <AuditLog />,
  },
  {
    id: 6,
    icon: <NotificationsNoneIcon />,
    name: <IntlMessages id='healthCare.notification' />,
    permissionKey: 'My Locker - Settings',
    component: () => <Notification />,
  },
  {
    id: 7,
    icon: <PendingActionsIcon />,
    name: verbiageData?.waiver_name || (
      <IntlMessages id='sidebar.product.waiversettings' />
    ),
    permissionKey: [
      'Company Settings - Waiver Settings',
      'Product Waivers Log',
      'Company Settings - Waiver Logs',
    ],
    component: () => <AllWaiverCombine />,
  },
  {
    id: 8,
    icon: <DisplaySettingsIcon />,
    name: <IntlMessages id='common.verbiageSettings' />,
    permissionKey: 'Company Settings - Verbiage',
    component: (props) => <VerbiageSettings {...props} />,
  },
  {
    id: 9,
    icon: <CurrencyExchangeIcon />,
    name: verbiageData?.convenience_fee_alias || (
      <IntlMessages id='common.convenienceFee' />
    ),
    permissionKey: 'Company Settings - Convenience Fee',
    component: () => <FeeSettings />,
  },
  {
    id: 10,
    icon: <DiscountIcon />,
    name: <IntlMessages id='sidebar.admin.promoCodeSettings' />,
    permissionKey: 'Company Settings - Promo Codes',
    component: () => <PromoCodes />,
  },
  ...(hasPaymentRecoveryAddon
  ? [
      {
        id: 11,
        icon: <PaymentRoundedIcon />,
        name: <IntlMessages id='sidebar.admin.paymentRecovery' />,
        permissionKey: 'Accounting - Missed Payments',
        component: () => <MissedPaymentSetting />,
      },
    ]
  : [])
]};

const Settings = () => {
  const { user } = useAuthUser();
  const { rolePermissions } = useRolePermissions();
  const [basicSettings, setBasicSettings] = useState(null);
  const [value, setValue] = useState(0);
  const { isMobile } = useMobileSettings();
  const { CheckAdddonsSettings, loading } = useAddonsSettings();
  const hasPaymentRecoveryAddon = CheckAdddonsSettings?.includes(paymentRecovery); // Check if payment recovery addon exists

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await jwtAxios.get(`/app-settings/`);
      setBasicSettings(response.data);
    } catch (error) {
      console.error('Error fetching appearance settings:', error);
    }
  };

  useEffect(() => {
    localStorage.setItem('timeZone', basicSettings?.basic?.timezone);
  }, [basicSettings]);

  const onTabsChange = (event, newValue) => {
    setValue(newValue);
  };
const menuTabs = !loading && tabs(hasPaymentRecoveryAddon);
  // Filter tabs based on permission
  let permittedTabs = !loading && menuTabs?.filter((tab) => {
    if (Array.isArray(tab.permissionKey)) {
      return tab.permissionKey.some((key) =>
        hasPermission(rolePermissions, key, 'read'),
      );
    }
    return tab.permissionKey ? hasPermission(rolePermissions, tab.permissionKey, 'read') : true;
  });

  // Append student-only "Profile Information" tab at the end
  if (isStudent(user) && hasPermission(rolePermissions, 'Product Profile Info', 'read')) {
    permittedTabs.push({
      id: 100,
      icon: <BiUser />,
      name: <IntlMessages id='common.profileInfo' />,
      permissionKey: 'Product Profile Info',
      component: () => <ProfileFiledData />, //NOSONAR
    });
  }

  if (isParent(user) && hasPermission(rolePermissions, 'My Locker - Account Info', 'read') ) {
    permittedTabs.push({
      id: 100,
      icon: <BiUser />,
      name: 'Linked Accounts',
      permissionKey: 'My Locker - Account Info',
      component: () => <LinkedAccounts />, //NOSONAR
    });
  }
  if (
    isStudent(user) && 
    (isActualAdmin(user) || isActualSuperAdmin(user)) && 
    hasPermission(rolePermissions, 'My Locker - Account Info', 'read')
  ) {
    permittedTabs.push({
      id: 100,
      icon: <BiUser />,
      name: 'Linked Accounts',
      permissionKey: 'My Locker - Account Info',
      component: () => <LinkedAccounts />, //NOSONAR
    });
  }
  

  if (isMobile) {
    permittedTabs = permittedTabs.filter(
      (tab) => tab.permissionKey === 'Product Profile Info',
    );
  }

  return (
    <>
     {!isMobile && <AppAnimate animation='transition.slideDownIn' delay={300}>
        <Box
          component='h2'
          sx={{
            fontSize: 16,
            color: 'text.primary',
            fontWeight: Fonts.SEMI_BOLD,
            mb: { xs: 2, lg: 4 },
          }}
        >
          My Account
        </Box>
      </AppAnimate>}

      <AccountTabsWrapper key='2'>
        <AppAnimate animation='transition.slideLeftIn' delay={300}>
          <Tabs
            className='account-tabs'
            value={value}
            onChange={onTabsChange}
            aria-label='basic tabs example'
            orientation='vertical'
          >
            {permittedTabs.map((tab, index) => (
              <Tab
                className='account-tab'
                label={tab.name}
                icon={tab.icon}
                key={index+0}
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        </AppAnimate>

        <AppAnimate animation='transition.slideRightIn' delay={300}>
          <Box className='account-tabs-content'>
            {permittedTabs.map((tab, index) => (
              <Box key={tab.id} hidden={value !== index}>
                {value === index &&
                  tab.component({ defaultSettings: basicSettings, fetchData })}
              </Box>
            ))}
          </Box>
        </AppAnimate>
      </AccountTabsWrapper>
    </>
  );
};

export default Settings;
